// @flow
import React, {useEffect} from "react";
import PriceOverviewContainer from "../../../container/checkout/dataSummaryBox/PriceOverviewContainer";
import i18n from "../../../i18next";
import type {PaymentFacility, PaymentMethod} from "../../../api/PaymentApi";
import "./Payment.scss";
import {PAYMENT_METHODS, PAYMENT_PROVIDER} from "../../../models/PaymentRequests";
import {toast} from "react-toastify";
import Loading from "../../layout/Loading";
import NextButton from "../NextButton";
import {useTranslation} from "react-i18next";
import usePayPalScript from "../../../hooks/usePayPalScript";

type PaymentProps = {
    isAllShipping: boolean,
    paymentMethods: PaymentMethod[],
    paymentFacility: PaymentFacility,
    paymentErrors: {},
    paymentAuthorizationFailed: boolean,
    orderIsCreated: boolean,
    orderPayedFullyByCoupon: boolean,
    setOrderFullyPayedByCoupon: () => void,
    assignPaymentMethod: (method: PaymentMethod) => void,
    nextPage: () => void,
};

const paymentHint = paymentMethod => {
    switch (paymentMethod) {
        case undefined:
            return "";
        case PAYMENT_METHODS.PREPAID:
            return "cart.processHint.step3.prepaid";
        case PAYMENT_METHODS.CASH:
            return "cart.processHint.step3.cash";
        case PAYMENT_METHODS.PAY_LATER:
            return "cart.processHint.step3.withoutAuthorization";
        default:
            return "cart.processHint.step3.withAuthorization";
    }
};

const getPaymentMethodClass = (paymentMethod: string) => {
    const language = i18n.language.slice(0, 2);
    switch (paymentMethod) {
        case PAYMENT_METHODS.PAYPAL:
            return "hpm-paypal";
        case PAYMENT_METHODS.PAY_LATER:
            return `hpm-pay-later ${language}`;
        case PAYMENT_METHODS.CARD:
            return "hpm-card";
        case PAYMENT_METHODS.SEPA:
            return `hpm-sepa ${language}`;
        case PAYMENT_METHODS.PREPAID:
            return `hpm-bankWire ${language}`;
        case PAYMENT_METHODS.PRZELEWY24:
            return "hpm-przelewy24";
        case PAYMENT_METHODS.CASH:
            return `hpm-euro ${language}`;
        default:
            return "";
    }
};

const Payment = ({
                     isAllShipping,
                     paymentMethods,
                     paymentFacility,
                     paymentErrors,
                     paymentAuthorizationFailed,
                     orderIsCreated,
                     orderPayedFullyByCoupon,
                     setOrderFullyPayedByCoupon,
                     assignPaymentMethod,
                     nextPage
                 }: PaymentProps) => {

    const {t} = useTranslation();
    const {isPaymentMethodAvailable} = usePayPalScript("capture");

    useEffect(() => {
        if (orderPayedFullyByCoupon) {
            setOrderFullyPayedByCoupon();
        }
    }, [orderPayedFullyByCoupon, setOrderFullyPayedByCoupon]);

    useEffect(() => {
        if (Object.keys(paymentErrors).length > 0) {
            toast.error(t("cart.paymentMethods.assignPaymentError"));
        }
    }, [paymentErrors, t]);

    useEffect(() => {
        if (paymentAuthorizationFailed) {
            toast.error(t("cart.paymentApiError"));
        }
    }, [paymentAuthorizationFailed, t])

    const handleSubmit = (method: PaymentMethod) => {
        assignPaymentMethod(method);
    };

    const isEqual = (method: PaymentMethod, facility: PaymentFacility) => {
        return (method.paymentMethod || "").toLowerCase() === ((facility || {}).paymentMethod || "").toLowerCase();
    };

    const isPayPalProvider = (provider: string) => {
        switch (provider.toLowerCase()) {
            case PAYMENT_PROVIDER.PAYPAL:
            case PAYMENT_PROVIDER.PAYPAL_EXPRESS:
            case PAYMENT_PROVIDER.PAYPAL_CAPTURE:
                return true;
            default:
                return false;
        }
    }

    const renderPaymentMethod = (payment: PaymentMethod) => {
        const paymentMethod = payment.paymentMethod;
        const paymentMethodClass = getPaymentMethodClass(paymentMethod);

        if(isPayPalProvider(payment.paymentProvider) && !isPaymentMethodAvailable(paymentMethod)) {
            return null;
        }

        if (paymentErrors[paymentMethod] === true) {
            return (
                <div key={paymentMethod} className="hpm-paymentFacility">
	                <button
                        type={"button"}
                        disabled={true}
                        className={`hpm-prepaidButton ${paymentMethodClass}`}
                    >
                        {t(`cart.paymentMethods.${paymentMethod}`)} {paymentErrors[paymentMethod]}
	                </button>
                </div>
            );
        }
        return (
            <div key={paymentMethod} className={`hpm-paymentFacility ${isEqual(payment, paymentFacility) ? " selected" : ""}`}>
	            <button
                    type={"button"}
                    onClick={() => handleSubmit(payment)}
                    className={`hpm-prepaidButton ${paymentMethodClass}`}
                >
		            {t(`cart.paymentMethods.${paymentMethod}`)}
	            </button>
            </div>
        );
    };

    return (
        <div className={"hpm-payment"}>
            <div className={"hpm-adressContainer"}>
                <div className="hpm-mainContentBox">
                    {(paymentFacility && paymentFacility.paymentMethod) && (
                        <div className="hint">
                            {t(paymentHint(paymentFacility.paymentMethod))}
                        </div>
                        )}
                    <div className={"hpm-adressTitle"}>
                        {t("cart.paymentMethods.paymentMethodCTA")}
                    </div>
                    {(orderIsCreated && !orderPayedFullyByCoupon)
                            ? <div className="hpm-menu hpm-payment">
                            {paymentMethods
                                    .filter(method => method.paymentMethod !== PAYMENT_METHODS.PAYPAL_EXPRESS)
                                    .filter(method => method.paymentMethod !== PAYMENT_METHODS.CASH || !isAllShipping)
                                    .map(method => renderPaymentMethod(method))
                            }
                            </div>
                            : <Loading/>
                        }

                </div>
                <div className="hpm-dataSummaryBox">
                    <div className={"hpm-dataSummaryBoxContent"}>
                        <PriceOverviewContainer hideSlyCoupon={true}/>
                        {paymentFacility &&
                            <div className={"hpm-dataSummaryBoxButtons"}>
                                <NextButton onClick={() => nextPage()}>
                                    {t("cart.paymentMethods.submit")}
                                </NextButton>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Payment;
